// contexts/MobileContext.tsx
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode
} from 'react';

// =============================================================================
// TYPES
// =============================================================================

export type BreakpointName = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

export type MobileTab = 'audit' | 'planner' | 'chat';

export interface VisibleColumns {
  audit: boolean;
  planner: boolean;
  chat: boolean;
}

export interface MobileContextValue {
  // Screen size booleans
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isTouchDevice: boolean;

  // Tab navigation
  activeTab: MobileTab;
  setActiveTab: (tab: MobileTab) => void;

  // Mobile menu
  isMobileMenuOpen: boolean;
  toggleMobileMenu: () => void;
  closeMobileMenu: () => void;

  // Column visibility
  visibleColumns: VisibleColumns;
  showTabBar: boolean;

  // Breakpoint values for reference
  breakpoints: typeof BREAKPOINTS;
}

// =============================================================================
// CONSTANTS
// =============================================================================

// Breakpoints matching Tailwind defaults
const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

// Tab types for mobile navigation
export const MOBILE_TABS = {
  AUDIT: 'audit' as const,
  PLANNER: 'planner' as const,
  CHAT: 'chat' as const,
};

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Custom hook for media query matching
 */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches);

    const handler = (event: MediaQueryListEvent) => setMatches(event.matches);

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

// =============================================================================
// CONTEXT
// =============================================================================

const MobileContext = createContext<MobileContextValue | null>(null);

/**
 * Hook to access mobile context
 */
export function useMobile(): MobileContextValue {
  const context = useContext(MobileContext);
  if (!context) {
    throw new Error('useMobile must be used within a MobileProvider');
  }
  return context;
}

// =============================================================================
// PROVIDER
// =============================================================================

interface MobileProviderProps {
  children: ReactNode;
}

/**
 * MobileProvider - Provides mobile-responsive state throughout the app
 */
export function MobileProvider({ children }: MobileProviderProps) {
  // Screen size detection
  const isMobile = useMediaQuery(`(max-width: ${BREAKPOINTS.md - 1}px)`);
  const isTablet = useMediaQuery(`(min-width: ${BREAKPOINTS.md}px) and (max-width: ${BREAKPOINTS.lg - 1}px)`);
  const isDesktop = useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px)`);

  // Touch device detection
  const isTouchDevice = useMediaQuery('(pointer: coarse)');

  // Active tab for mobile navigation (default to planner)
  const [activeTab, setActiveTab] = useState<MobileTab>(MOBILE_TABS.PLANNER);

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Close mobile menu when switching to desktop
  useEffect(() => {
    if (isDesktop) {
      setIsMobileMenuOpen(false);
    }
  }, [isDesktop]);

  // Tab navigation handlers
  const navigateToTab = useCallback((tab: MobileTab) => {
    setActiveTab(tab);
    // Close menu when navigating
    setIsMobileMenuOpen(false);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  // Determine which columns should be visible based on screen size and active tab
  const visibleColumns = useMemo((): VisibleColumns => {
    if (isDesktop) {
      // Desktop: all three columns visible
      return { audit: true, planner: true, chat: true };
    }

    if (isTablet) {
      // Tablet: Planner + Chat visible, Audit via tab
      return {
        audit: activeTab === MOBILE_TABS.AUDIT,
        planner: activeTab !== MOBILE_TABS.AUDIT,
        chat: activeTab !== MOBILE_TABS.AUDIT,
      };
    }

    // Mobile: only active tab visible
    return {
      audit: activeTab === MOBILE_TABS.AUDIT,
      planner: activeTab === MOBILE_TABS.PLANNER,
      chat: activeTab === MOBILE_TABS.CHAT,
    };
  }, [isDesktop, isTablet, activeTab]);

  // Calculate if we need to show the tab bar
  // Only show on actual mobile/tablet devices (touch), not just narrow desktop windows
  const showTabBar = !isDesktop && isTouchDevice;

  const value = useMemo((): MobileContextValue => ({
    // Screen size booleans
    isMobile,
    isTablet,
    isDesktop,
    isTouchDevice,

    // Tab navigation
    activeTab,
    setActiveTab: navigateToTab,

    // Mobile menu
    isMobileMenuOpen,
    toggleMobileMenu,
    closeMobileMenu,

    // Column visibility
    visibleColumns,
    showTabBar,

    // Breakpoint values for reference
    breakpoints: BREAKPOINTS,
  }), [
    isMobile,
    isTablet,
    isDesktop,
    isTouchDevice,
    activeTab,
    navigateToTab,
    isMobileMenuOpen,
    toggleMobileMenu,
    closeMobileMenu,
    visibleColumns,
    showTabBar,
  ]);

  return (
    <MobileContext.Provider value={value}>
      {children}
    </MobileContext.Provider>
  );
}

export default MobileContext;
